import React, { useEffect } from 'react';

import { Box, Divider, Typography, Stack } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import AccordionCard from 'common/components/accordions/AccordionCard';
import {
    ReactHookFormCheck,
    ReactHookFormIntlNumberInput,
    ReactHookFormSelect,
} from 'common/components/form';
import TitleWithDetail from 'common/components/TitleWithDetail';
import { getAlertConditionOptions, getAlertTitle } from 'common/utils/helpers';

import * as actions from '../actions';
import * as selectors from '../selectors';

import RoiFields from './RoiFields';

const Alerts = ({
    control,
    disabled,
    fetchIncentiveOfferComplements,
    incentiveOfferComplementsData,
    isFetching,
    setValue,
}) => {
    const { t } = useTranslation();

    const [alerts, isAmountActive, isPowerActive] = useWatch({
        control,
        name: ['alerts', 'amount_values.isChecked', 'power_values.isChecked'],
    });

    const convertAlertsToArray = (alerts, category) => {
        return Object.keys(alerts)
            .filter((key) => alerts[key].category === category)
            .map((key) => ({
                name: key,
                title: getAlertTitle(key),
                options: getAlertConditionOptions(key),
            }));
    };

    const adjustableAlertsArray = convertAlertsToArray(alerts, 'adjustable');
    const nonAdjustableAlertsArray = convertAlertsToArray(
        alerts,
        'non-adjustable',
    );

    const handleConditionChange = (alertName, value) => {
        const options = getAlertConditionOptions(alertName);
        const selectedOption = options.find((opt) => opt.value === value);
        if (selectedOption) {
            setValue(
                `alerts.${alertName}.is_mandatory`,
                selectedOption.is_mandatory,
            );
            setValue(`alerts.${alertName}.type`, selectedOption.type);
        }
    };

    const handleOnBlurField = ({ name, value }) => {
        if (!value) setValue(name, 0);
    };

    useEffect(() => {
        fetchIncentiveOfferComplements();
    }, []);

    return (
        <Box>
            <Typography fontWeight={600} variant="body2" mb={2}>
                {t('Configure your alerts')}
            </Typography>
            <Divider sx={{ my: 1 }} />

            <AccordionCard
                defaultExpanded
                customTitle={
                    <TitleWithDetail variant="body2">
                        {t('Adjustable Rules')}
                    </TitleWithDetail>
                }
                sxCard={{ boxShadow: 'none', mb: 0 }}
            >
                {adjustableAlertsArray.map(({ name, title, options }) => {
                    const [isActive, condition] = useWatch({
                        control,
                        name: [
                            `alerts.${name}.is_active`,
                            `alerts.${name}.condition`,
                        ],
                    });
                    return (
                        <Box
                            component="fieldset"
                            key={name}
                            sx={{
                                borderColor: 'divider',
                                borderRadius: 1,
                                borderWidth: isActive ? 1 : 0,
                                m: 0,
                                p: 0,
                            }}
                        >
                            <Box component="legend" sx={{ m: 0, p: 0 }}>
                                <ReactHookFormCheck
                                    control={control}
                                    disabled={disabled}
                                    label={title}
                                    name={`alerts.${name}.is_active`}
                                />
                            </Box>

                            {isActive && (
                                <Stack sx={{ p: 1 }}>
                                    <ReactHookFormSelect
                                        control={control}
                                        disabled={disabled}
                                        label={t('Type')}
                                        name={`alerts.${name}.condition`}
                                        options={options.map((option) => ({
                                            value: option.value,
                                            label: option.label,
                                        }))}
                                        onChange={(e) => {
                                            handleConditionChange(
                                                name,
                                                e.target.value,
                                            );
                                        }}
                                    />

                                    {[
                                        'supplier_annual_increase',
                                        'supplier_VAT',
                                    ].includes(name) && (
                                        <ReactHookFormIntlNumberInput
                                            allowNegativeValue={false}
                                            append="%"
                                            control={control}
                                            disabled={disabled}
                                            fullWidth
                                            label={t('Percentage')}
                                            min={0}
                                            name={`alerts.${name}.value_100`}
                                            onBlur={({ value }) => {
                                                setValue(
                                                    `alerts.${name}.value`,
                                                    value / 100,
                                                );
                                            }}
                                            step="any"
                                        />
                                    )}

                                    {name === 'tax_incentive' &&
                                        condition ===
                                            'Requires that the tax incentive is enabled' && (
                                            <Box sx={{ width: '100%' }}>
                                                <RoiFields
                                                    control={control}
                                                    disabled={disabled}
                                                    incentiveOfferComplementsData={
                                                        incentiveOfferComplementsData
                                                    }
                                                    isFetching={isFetching}
                                                    name={name}
                                                    setValue={setValue}
                                                />
                                            </Box>
                                        )}
                                </Stack>
                            )}
                        </Box>
                    );
                })}
            </AccordionCard>

            <AccordionCard
                defaultExpanded
                customTitle={
                    <TitleWithDetail variant="body2">
                        {t('Non-Adjustable Rules')}
                    </TitleWithDetail>
                }
                sxCard={{ boxShadow: 'none', mb: 0 }}
            >
                {nonAdjustableAlertsArray.map(({ name, title, options }) => {
                    const [isActive] = useWatch({
                        control,
                        name: [`alerts.${name}.is_active`],
                    });
                    return (
                        <Box
                            component="fieldset"
                            key={name}
                            sx={{
                                borderColor: 'divider',
                                borderRadius: 1,
                                borderWidth: isActive ? 1 : 0,
                                m: 0,
                                p: 0,
                            }}
                        >
                            <Box component="legend" sx={{ m: 0, p: 0 }}>
                                <ReactHookFormCheck
                                    control={control}
                                    disabled={disabled}
                                    label={title}
                                    name={`alerts.${name}.is_active`}
                                />
                            </Box>

                            {isActive && (
                                <Stack sx={{ p: 1 }}>
                                    <ReactHookFormSelect
                                        control={control}
                                        disabled={disabled}
                                        label={t('Type')}
                                        name={`alerts.${name}.condition`}
                                        options={options.map((option) => ({
                                            value: option.value,
                                            label: option.label,
                                        }))}
                                        onChange={(e) => {
                                            handleConditionChange(
                                                name,
                                                e.target.value,
                                            );
                                        }}
                                    />
                                </Stack>
                            )}
                        </Box>
                    );
                })}

                <Box
                    component="fieldset"
                    sx={{
                        borderColor: 'divider',
                        borderRadius: 1,
                        borderWidth: isAmountActive ? 1 : 0,
                        m: 0,
                        p: 0,
                    }}
                >
                    <Box component="legend" sx={{ m: 0, p: 0 }}>
                        <ReactHookFormCheck
                            control={control}
                            disabled={disabled}
                            label={t('Amount limit')}
                            name="amount_values.isChecked"
                        />
                    </Box>

                    {isAmountActive && (
                        <Stack sx={{ p: 1 }}>
                            <ReactHookFormIntlNumberInput
                                append="MXN"
                                control={control}
                                disabled={disabled}
                                fullWidth
                                label={t('Minimum amount')}
                                name="amount_values.min"
                                prepend="$"
                                onBlur={({ value }) =>
                                    handleOnBlurField({
                                        name: 'amount_values.min',
                                        value,
                                    })
                                }
                            />

                            <ReactHookFormIntlNumberInput
                                append="MXN"
                                control={control}
                                disabled={disabled}
                                fullWidth
                                label={t('Maximum amount')}
                                name="amount_values.max"
                                prepend="$"
                                onBlur={({ value }) =>
                                    handleOnBlurField({
                                        name: 'amount_values.max',
                                        value,
                                    })
                                }
                            />
                        </Stack>
                    )}
                </Box>

                <Box
                    component="fieldset"
                    sx={{
                        borderColor: 'divider',
                        borderRadius: 1,
                        borderWidth: isPowerActive ? 1 : 0,
                        m: 0,
                        p: 0,
                    }}
                >
                    <Box component="legend" sx={{ m: 0, p: 0 }}>
                        <ReactHookFormCheck
                            control={control}
                            disabled={disabled}
                            label={t('Power limit')}
                            name="power_values.isChecked"
                        />
                    </Box>

                    {isPowerActive && (
                        <Stack sx={{ p: 1 }}>
                            <ReactHookFormIntlNumberInput
                                append="kW"
                                control={control}
                                disabled={disabled}
                                fullWidth
                                label={t('Minimum power')}
                                name="power_values.min"
                                onBlur={({ value }) =>
                                    handleOnBlurField({
                                        name: 'power_values.min',
                                        value,
                                    })
                                }
                            />

                            <ReactHookFormIntlNumberInput
                                append="kW"
                                control={control}
                                disabled={disabled}
                                fullWidth
                                label={t('Maximum power')}
                                name="power_values.max"
                                onBlur={({ value }) =>
                                    handleOnBlurField({
                                        name: 'power_values.max',
                                        value,
                                    })
                                }
                            />
                        </Stack>
                    )}
                </Box>
            </AccordionCard>
        </Box>
    );
};

Alerts.propTypes = {
    control: PropTypes.object,
    disabled: PropTypes.bool,
    fetchIncentiveOfferComplements: PropTypes.func,
    incentiveOfferComplementsData: PropTypes.array,
    isFetching: PropTypes.bool,
    setValue: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
    incentiveOfferComplementsData: selectors.getIncentiveOfferComplementsData,
    isFetching: selectors.getIsFetchingIncentiveOfferComplements,
});

const mapDispatchToProps = (dispatch) => ({
    fetchIncentiveOfferComplements: () =>
        dispatch(actions.fetchIncentiveOfferComplements()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Alerts);
